import { useState } from 'react';
import {
  Alert,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Boxes, Mail } from 'lucide-react';
import { useAccount } from '@azure/msal-react';

import { createWorkflow, toWorkflowActor } from '$api/WorkflowsAPI';
import { useAccessToken } from '$src/authConfig';

import { Modal } from './Modal';
import { Stack } from './Stack';

const ID_RE = /^[A-Za-z0-9_]+$/;
const TRIGGERS = [
  {
    id: 'email',
    name: 'Email Trigger',
    description:
      'Workflow triggered by incoming email. Routable from a mailbox.',
  },
  {
    id: 'workflow',
    name: 'Workflow Trigger',
    description:
      'Reusable building block that can be embedded as a step in any other workflow.',
  },
] as const;

function TriggerIcon({ id, size }: { id: string; size: number }) {
  return id === 'workflow' ? <Boxes size={size} /> : <Mail size={size} />;
}

export function CreateWorkflowModal({
  defaultTriggerId,
  onCancel,
  onCreated,
}: {
  defaultTriggerId: string;
  onCancel: () => void;
  onCreated: (workflowId: string) => void;
}) {
  const [id, setId] = useState('');
  const { token } = useAccessToken();
  const account = useAccount();
  const [title, setTitle] = useState('');
  const [trigger, setTrigger] = useState(defaultTriggerId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = id.trim();
  const idError =
    trimmed && !ID_RE.test(trimmed)
      ? 'Use letters, numbers and underscores only.'
      : null;
  const canCreate = Boolean(trimmed) && !idError && !busy;

  const create = async () => {
    if (!canCreate || !token) return;
    setBusy(true);
    setError(null);
    try {
      await createWorkflow(token, {
        workflowId: trimmed,
        title: title.trim() || trimmed,
        trigger,
        actor: toWorkflowActor(account),
      });
      onCreated(trimmed);
    } catch (e) {
      setError(String(e));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New workflow"
      onClose={onCancel}
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" disabled={!canCreate} onClick={create}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={trigger}
        onChange={(_, v) => v && setTrigger(v)}
        orientation="vertical"
        sx={{ gap: 1 }}
      >
        {TRIGGERS.map((tpl) => (
          <ToggleButton
            key={tpl.id}
            value={tpl.id}
            sx={{
              display: 'block',
              textAlign: 'left',
              textTransform: 'none',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TriggerIcon id={tpl.id} size={16} />
              <Typography sx={{ fontWeight: 600 }}>{tpl.name}</Typography>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {tpl.description}
            </Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <TextField
        label="Workflow id"
        autoFocus
        placeholder="e.g. invoices"
        value={id}
        onChange={(e) => setId(e.target.value)}
        error={Boolean(idError)}
        helperText={idError ?? ' '}
      />
      <TextField
        label="Title"
        placeholder={trimmed || 'Title'}
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      {error && <Alert severity="error">{error}</Alert>}
    </Modal>
  );
}
